<template>
  <v-dialog v-model="localDisplay" width="700">
    <v-card class="rounded-lg" flat>
      <v-card-title class="justify-center text-color mt-2">
        <span v-if="value.type === 'create'">
          {{ $t('pricing_model.promotional_codes.create.title') }}
        </span>
        <span v-else>
          {{ $t('pricing_model.promotional_codes.edit.title') }}
        </span>
      </v-card-title>
      <v-form v-model="valid" ref="create-form" lazy-validation>
        <v-card-text>
          <v-row no-gutters class="mt-4">
            <!------------------------------------------>
            <!-- NAME ---------------------------------->
            <!------------------------------------------>
            <v-col cols="12">
              <v-text-field
                v-model="form.name"
                :label="$t('common.name')"
                :rules="rules.name"
                class="rounded-lg font-weight-regular"
                outlined
                dense
              >
              </v-text-field>
            </v-col>
            <!------------------------------------------>
            <!-- CODE ---------------------------------->
            <!------------------------------------------>
            <v-col cols="12">
              <v-text-field
                v-model="form.code"
                :label="$t('pricing_model.promotional_codes.fields.code')"
                :rules="rules.code"
                class="rounded-lg font-weight-regular"
                outlined
                dense
              >
              </v-text-field>
            </v-col>
            <!------------------------------------------>
            <!-- EXPIRATION DATE ----------------------->
            <!------------------------------------------>
            <v-col cols="12">
              <v-menu
                ref="expiration_date_menu"
                v-model="expiration_date_menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.expiration_date"
                    v-bind="attrs"
                    v-on="on"
                    :label="
                      $t(
                        'pricing_model.promotional_codes.fields.expiration_date'
                      )
                    "
                    :rules="rules.expiration_date"
                    prepend-inner-icon="calendar_today"
                    class="rounded-lg font-weight-regular"
                    outlined
                    dense
                    readonly
                  >
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="form.expiration_date"
                  :min="new Date(Date.now()).toISOString().substring(0, 10)"
                  color="#2661FF"
                  @change="saveExpirationDate"
                />
              </v-menu>
            </v-col>
            <!------------------------------------------>
            <!-- STATUS -------------------------------->
            <!------------------------------------------>
            <v-col cols="12">
              <v-select
                v-model="form.status"
                :items="statuses"
                :label="$t('pricing_model.promotional_codes.fields.status')"
                :rules="rules.status"
                item-text="text"
                item-value="value"
                class="rounded-lg font-weight-regular"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-end mx-2">
          <v-btn
            color="#2661FF"
            outlined
            class="rounded-lg"
            @click="closeDialog"
          >
            {{ $t('actions.cancel') }}
          </v-btn>
          <v-btn
            color="#2661FF"
            class="rounded-lg white--text"
            @click="savePromotionalCode"
            :disabled="!valid"
            :loading="loading"
          >
            {{ $t('actions.save') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'HandlePromotionalCodeDialog',
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      valid: false,
      expiration_date_menu: false,
      form: {
        id: null,
        name: null,
        code: null,
        status: null,
        expiration_date: null,
        pricing_model_id: null
      },
      rules: {
        name: [
          v =>
            !!v ||
            this.$t('messages.validation_error.required', {
              field: this.$t('common.name')
            })
        ],
        code: [
          v =>
            !!v ||
            this.$t('messages.validation_error.required', {
              field: this.$t('pricing_model.promotional_codes.fields.code')
            })
        ],
        expiration_date: [
          v =>
            !!v ||
            this.$t('messages.validation_error.required', {
              field: this.$t(
                'pricing_model.promotional_codes.fields.expiration_date'
              )
            })
        ],
        status: [
          v =>
            !!v ||
            this.$t('messages.validation_error.required', {
              field: this.$t('pricing_model.promotional_codes.fields.status')
            })
        ]
      },
      statuses: [
        {
          text: this.$t('common.active'),
          value: 1
        },
        {
          text: this.$t('common.inactive'),
          value: 2
        }
      ]
    }
  },
  created() {
    const { value } = this
    const { item } = value

    this.form.pricing_model_id = value.pricing_model_id

    if (value.type === 'edit') {
      this.form = {
        id: item.id,
        name: item.name,
        code: item.code,
        status: item.status,
        pricing_model_id: item.pricing_model_id,
        expiration_date: item.expiration_date.substring(0, 10)
      }
    }
  },
  computed: {
    ...mapState({
      pricingModels: state => state.pricingModels.pricingModels
    }),
    localDisplay: {
      get() {
        return this.value.display
      },
      set(value) {
        this.$emit('update:display', value)
      }
    }
  },
  methods: {
    ...mapActions({
      editPromotionalCode: 'pricingModels/editPromotionalCode',
      createPromotionalCode: 'pricingModels/createPromotionalCode'
    }),
    saveExpirationDate() {
      this.$refs.expiration_date_menu.save(this.form.expiration_date)
    },
    async savePromotionalCode() {
      const { value } = this

      const formRef = this.$refs['create-form']

      const validation = formRef.validate()

      if (!validation) return

      this.loading = true

      switch (value.type) {
        case 'create':
          await this.createPromotionalCode(this.form)
          break
        case 'edit':
          await this.editPromotionalCode(this.form)
          break
      }

      await this.$emit('fetchPromotionalCodes')
      this.closeDialog()
      this.loading = false
    },
    closeDialog() {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss">
.text-color {
  color: #344054 !important;
}
</style>
